import React, { useContext, useEffect, useState } from 'react'
import { cloneDeep } from 'lodash'
import { Trans, t } from '@lingui/macro'
import { Box } from '@chakra-ui/react'

import { useListGroupsQuery } from 'front-end/src/pages/manage-pathways-page/components/PathwayShareModal.codegen'
import DrawerItem from 'front-end/src/components/common/page/drawer/DrawerItem'

import IconMinus from '../../../../components/common/icon/IconMinus'
import IconPlane from '../../../../components/common/icon/IconPlane'
import IconPlus from '../../../../components/common/icon/IconPlusInCircle'
import Loader from '../../../../components/common/loader/Loader'
import MembersSelector, {
  IMembersSelectorGroup,
  IMembersSelectorMembers,
  IMembersSelectorUser
} from '../../../../components/common/members-selector/MembersSelector'
import Textarea from '../../../../components/common/form/textarea/Textarea'
import userSearch from '../../../../operations/userSearch'
import {
  ButtonClean,
  ButtonPrimary,
  Switch,
  Modal
} from 'front-end/src/components'
import { FormRow } from '../../../../components/common/form/Form'

import { notification } from '../../../../components/common/notifications/Notifications'
import { useSendSurveyMutation } from './SurveySendModal.codegen'
import { SenderSelect } from '../../../manage-campaign-page/campaign-new-action/SenderSelect'

import './SurveySendModal.less'
import { ManageSurveyContext } from '../ManageSurveyContext'

interface ISurveySendAddModalProps {
  show: boolean
  onCancel: () => void
  onSubmitted: () => void
}

const emptyMembers = {
  users: [],
  groups: []
}

export const SurveySendModal = ({
  show,
  onCancel,
  onSubmitted
}: ISurveySendAddModalProps) => {
  const { survey } = useContext(ManageSurveyContext)
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [isSearching, setIsSearching] = useState<boolean>(false)
  const [search, setSearch] = useState<string>('')
  const [noteText, setNoteText] = useState<string>('')
  const [noteTextHidden, setNoteTextHidden] = useState<boolean>(true)
  const [membersSearched, setMembersSearched] =
    useState<IMembersSelectorMembers>(cloneDeep(emptyMembers))
  const [membersSelected, setMembersSelected] =
    useState<IMembersSelectorMembers>(cloneDeep(emptyMembers))
  const [sendSurvey] = useSendSurveyMutation()
  const [sendEmail, setSendEmail] = useState(false)
  const [emailIdentityId, setEmailIdentityId] = useState<number | null>(null)

  const send = async (
    membersSelected: IMembersSelectorMembers,
    noteText: string
  ) => {
    const { data } = await sendSurvey({
      variables: {
        surveyId: survey.id,
        input: {
          users: membersSelected.users.map((user) => user.id),
          groups: membersSelected.groups.map((group) => group.id),
          sendEmail: sendEmail,
          emailIdentityId: emailIdentityId,
          noteText: noteText
        }
      }
    })

    if (data && data.admin?.survey) {
      onCancel()
      await onSubmitted()
      if (data.admin?.survey.send === 0) {
        notification.error(
          t`Nobody added, all users are already in recipient list.`
        )
      } else {
        notification.info(t`Added ${data.admin?.survey.send} users`)
      }
    }
  }

  useEffect(() => {
    if (search) {
      fetchUsers()
    } else {
      setMembersSearched(cloneDeep(emptyMembers))
    }
  }, [search])

  // Get all groups
  const groupsQueryResponse = useListGroupsQuery()

  if (!groupsQueryResponse || !groupsQueryResponse.data) {
    return null
  }

  if (groupsQueryResponse.loading) {
    return <Loader />
  }

  const memberGroups = groupsQueryResponse.data?.groups
  const memberUsers = []

  const fetchUsers = async () => {
    try {
      const filters = {
        filterUsers: [
          { name: 'notArchived' },
          { name: 'search', value: search }
        ]
      }

      const searchedUsers = await userSearch(filters)

      const searchedGroups = memberGroups?.filter((row) => {
        return row.name.toLowerCase().includes(search.toLowerCase())
      })

      setMembersSearched({ users: searchedUsers, groups: searchedGroups ?? [] })
    } catch (err) {
      notification.error(t`Search failed`)
      console.warn(err)
    } finally {
      setIsSearching(false)
    }
  }

  const handleAdd = (
    user?: IMembersSelectorUser,
    group?: IMembersSelectorGroup
  ): void => {
    const tempList = membersSelected

    if (user) {
      tempList.users.push(user)
    }

    if (group) {
      tempList.groups.push(group)
    }

    setMembersSelected({ ...tempList })
  }

  const handleRemove = (
    user?: IMembersSelectorUser,
    group?: IMembersSelectorGroup
  ): void => {
    const tempList = membersSelected

    if (user) {
      tempList.users.splice(
        tempList.users.findIndex((i) => i.id === user.id),
        1
      )
    }
    if (group) {
      tempList.groups.splice(
        tempList.groups.findIndex((i) => i.id === group.id),
        1
      )
    }

    setMembersSelected({ ...tempList })
  }

  return (
    <>
      <Modal
        className="GenericAccessModal SurveySendAddModal"
        header={t`Add people and groups`}
        show={show}
        onShown={() => setSubmitting(false)}
        onHidden={() => {
          setNoteTextHidden(true)
          setMembersSelected(cloneDeep(emptyMembers))
        }}
        controls={
          <>
            <ButtonClean onClick={onCancel}>Cancel</ButtonClean>
            <ButtonPrimary
              disabled={
                (membersSelected.users.length === 0 &&
                  membersSelected.groups.length === 0) ||
                submitting
              }
              isLoading={submitting}
              onClick={() => {
                send(membersSelected, noteText)
              }}
              leftIcon={<IconPlane />}
            >
              <Trans>Share</Trans>
            </ButtonPrimary>
          </>
        }
      >
        <MembersSelector
          isSearching={isSearching}
          addressBook={{ users: memberUsers, groups: memberGroups ?? [] }}
          membersSelected={membersSelected}
          membersSearched={membersSearched}
          onAdd={(user, group) =>
            handleAdd(user as IMembersSelectorUser, group)
          }
          onRemove={(user, group) =>
            handleRemove(user as IMembersSelectorUser, group)
          }
          onQueryChange={(e) => {
            setIsSearching(true)
            setSearch(e)
          }}
        />

        <Box className="toggleRow">
          <DrawerItem label={t`Send share emails`}>
            <Switch
              isChecked={sendEmail}
              onChange={() => {
                setSendEmail(!sendEmail)
              }}
            />
          </DrawerItem>
        </Box>

        {sendEmail && (
          <Box className="toggleRow">
            <div className="SurveySendAddModal__customMessage custom-message">
              <SenderSelect
                value={emailIdentityId}
                onChange={(v) => setEmailIdentityId(v)}
              />
              <ButtonClean
                size="sm"
                onClick={() => {
                  setNoteTextHidden(!noteTextHidden)
                  setNoteText('')
                }}
                leftIcon={noteTextHidden ? <IconPlus /> : <IconMinus />}
              >
                {noteTextHidden ? (
                  <Trans>Add message</Trans>
                ) : (
                  <Trans>Remove message</Trans>
                )}
              </ButtonClean>

              {!noteTextHidden && (
                <FormRow>
                  <Textarea
                    placeholder={t`Add a message...`}
                    value={noteText}
                    onChange={(value: string): void => setNoteText(value)}
                  />
                </FormRow>
              )}
            </div>
          </Box>
        )}
      </Modal>
    </>
  )
}
